import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { Form, Modal, Row, Col, Toast, ToastContainer } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import * as yup from 'yup';
import { FaCheck } from 'react-icons/fa';
import { sendGenericMessage, getConnectedUsers } from '../../rest/messages';
import { handleError, isNotBusinessError } from '../../utils/handleError';
import { TextAreaInputField } from '../bootstrap/TextAreaInputField';
import { TextInputField } from '../bootstrap/TextInputField';

export function GeneralMessageModal({ show, setShow }) {
  const intl = useIntl();
  const [showSaveToast, setShowSaveToast] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [error, setError] = useState(null);

  const messageToSendStructure = { to: '', subject: '', body: '' };

  async function handleSubmit(values, formikObj) {
  try {
    const recipient = recipients.find((r) => String(r.id) === values.to);

    const reqBody = {
      ...values,
      to: recipient, // full object again
      title: values.subject,
    };
    delete reqBody.subject;

    await sendGenericMessage(reqBody);

    formikObj.setSubmitting(false);
    setShowSaveToast(true);
    setShow(false);
  } catch (error) {
    setError(error);
  }
}

  function handleClose() {
    setShow(false);
  }

  useEffect(() => {
    async function fetchRecipients() {
      try {
        const data = await getConnectedUsers();
        setRecipients(data?.data || []);
      } catch (err) {
        console.error('Error fetching recipients:', err);
        setError(err);
      }
    }
    fetchRecipients();
  }, []);

  if (error && isNotBusinessError(error)) {
    return handleError(error);
  }

  const messageGenericFormSchema = yup.object().shape({
  to: yup
    .string()
    .required(<FormattedMessage id="errors.fieldRequiredText" />),
  subject: yup
    .string()
    .required(<FormattedMessage id="errors.fieldRequiredText" />)
    .max(100, <FormattedMessage id="errors.messagePriorSupportEntityForm.subject" />),
  body: yup
    .string()
    .required(<FormattedMessage id="errors.fieldRequiredText" />)
    .max(500, <FormattedMessage id="errors.messagePriorSupportEntityForm.body" />),
});

  return (
    <div>
      <ToastContainer className='fixed-top'>
        <Toast
          autohide
          delay={3000}
          show={showSaveToast}
          onClose={() => setShowSaveToast(false)}
        >
          <Toast.Header>
            <strong className='me-auto text-primary'>
              <FaCheck className='me-2' />
              <FormattedMessage id='message.success' />
            </strong>
          </Toast.Header>
        </Toast>
      </ToastContainer>

      <Modal
        show={show}
        onHide={handleClose}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FormattedMessage id='messagesNotification.GenericMessageModal.title' />
          </Modal.Title>
        </Modal.Header>

        <Formik
          initialValues={messageToSendStructure}
          validationSchema={messageGenericFormSchema}
          validateOnBlur={false}
          validateOnChange={false}
          enableReinitialize // important when recipients load async
          onSubmit={handleSubmit}
        >
          {(formikProps) => (
            <Form onSubmit={formikProps.handleSubmit}>
              <Modal.Body>
                <Row>
                  {/* Recipient Select */}
                  <Col md='12'>
                    <Form.Label>
                      {intl.formatMessage({ id: "messages.toText" })}
                    </Form.Label>
                    <Form.Select
                      name='to'
                      value={formikProps.values.to || ""}
                      onChange={(e) => {
                        formikProps.setFieldValue("to", e.target.value);
                      }}
                    >
                      <option value=''>
                        {intl.formatMessage({
                          id: "messagesNotification.messageForm.to.placeholder",
                        })}
                      </option>
                      {recipients.map((option) => (
                        <option
                          key={option.id}
                          value={String(option.id)}
                        >
                          {option.name} ({option.email})
                        </option>
                      ))}
                    </Form.Select>
                    {formikProps.errors.to && (
                      <div className='invalid-feedback'>{formikProps.errors.to}</div>
                    )}
                  </Col>

                  {/* Subject */}
                  <Col
                    md='12'
                    className='mt-3'
                  >
                    <TextInputField
                      placeholder={intl.formatMessage({
                        id: "candidatureForm.PriorSupportMessageModal.messageForm.subject.placeholder",
                      })}
                      labelId='candidatureForm.PriorSupportMessageModal.messageForm.subject'
                      name='subject'
                      value={formikProps.values.subject}
                      isInvalid={formikProps.errors.subject}
                      errorMessage={formikProps.errors.subject}
                      handleChange={formikProps.handleChange}
                    />
                  </Col>

                  {/* Body */}
                  <Col
                    md='12'
                    className='mt-3'
                  >
                    <TextAreaInputField
                      placeholder={intl.formatMessage({
                        id: "candidatureForm.PriorSupportMessageModal.messageForm.body.placeholder",
                      })}
                      labelId='candidatureForm.PriorSupportMessageModal.messageForm.body'
                      name='body'
                      value={formikProps.values.body}
                      isInvalid={formikProps.errors.body}
                      errorMessage={formikProps.errors.body}
                      handleChange={formikProps.handleChange}
                    />
                  </Col>
                </Row>
              </Modal.Body>

              <Modal.Footer className='d-flex justify-content-between'>
                <button
                  className='btn btn-outline-primary px-5'
                  onClick={handleClose}
                  type='button'
                >
                  <FormattedMessage id='all.cancel' />
                </button>
                <button
                  className='btn btn-primary px-5'
                  type='submit'
                >
                  <FormattedMessage id='all.send' />
                </button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>
    </div>
  );
}
